import React, { useState, useEffect } from "react";
import {
  FaGavel,
  FaBriefcase,
  FaFileAlt,
  FaChartLine,
  FaUsers,
  FaTrophy,
  FaHistory,
  FaWallet,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const AdvocateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [stats, setStats] = useState({
    totalCases: 0,
    activeCases: 0,
    completedCases: 0,
    pendingCases: 0,
    totalClients: 0,
    walletBalance: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get user info from localStorage
        const user = localStorage.getItem('user');
        if (user) {
          setUserInfo(JSON.parse(user));
        }

        // TODO: Fetch actual case data from API when backend is ready
        // const response = await axios.get(
        //   `${process.env.REACT_APP_API_BASE_URL}/advocate/cases`,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${localStorage.getItem("token")}`,
        //     },
        //   }
        // );

        // For now, use mock data
        setStats({
          totalCases: 12,
          activeCases: 5,
          completedCases: 6,
          pendingCases: 1,
          totalClients: 8,
          walletBalance: 0,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const cards = [
    {
      title: "My Cases",
      icon: <FaGavel className="w-6 h-6" />,
      count: stats.totalCases,
      color: "from-red-500 to-red-600",
      onClick: () => navigate("/advocate/cases"),
      description: "Manage your legal cases",
      trend: null,
    },
    {
      title: "Active Cases",
      icon: <FaBriefcase className="w-6 h-6" />,
      count: stats.activeCases,
      color: "from-blue-500 to-blue-600",
      onClick: () => navigate("/advocate/cases"),
      description: "Currently active cases",
      trend: null,
    },
    {
      title: "Documents",
      icon: <FaFileAlt className="w-6 h-6" />,
      count: "View",
      color: "from-purple-500 to-purple-600",
      onClick: () => navigate("/advocate/documents"),
      description: "Manage legal documents",
      trend: null,
    },
    {
      title: "Analytics",
      icon: <FaChartLine className="w-6 h-6" />,
      count: "View",
      color: "from-green-500 to-green-600",
      onClick: () => navigate("/advocate/analytics"),
      description: "View case analytics",
      trend: null,
    },
    {
      title: "Payment History",
      icon: <FaHistory className="w-6 h-6" />,
      count: "View",
      color: "from-indigo-500 to-indigo-600",
      onClick: () => navigate("/advocate/history"),
      description: "View payment history",
      trend: null,
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen overflow-x-hidden">
      {/* Header Section */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
          Welcome back, {userInfo?.name || "Advocate"}!
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Manage your legal practice and cases effectively
        </p>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
        {/* Total Cases */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-full bg-red-100 flex-shrink-0">
              <FaGavel className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalCases}</p>
              <p className="text-xs sm:text-sm text-gray-500">Total Cases</p>
              <div className="flex flex-wrap gap-1 sm:gap-2 mt-1">
                <span className="text-xs bg-blue-100 text-blue-800 px-1 sm:px-2 py-1 rounded">
                  {stats.activeCases} Active
                </span>
                <span className="text-xs bg-green-100 text-green-800 px-1 sm:px-2 py-1 rounded">
                  {stats.completedCases} Completed
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Cases */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-full bg-blue-100 flex-shrink-0">
              <FaBriefcase className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.activeCases}</p>
              <p className="text-xs sm:text-sm text-gray-500">Active Cases</p>
              <p className="text-xs text-gray-400 mt-1">
                Currently in progress
              </p>
            </div>
          </div>
        </div>

        {/* Total Clients */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-full bg-purple-100 flex-shrink-0">
              <FaUsers className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalClients}</p>
              <p className="text-xs sm:text-sm text-gray-500">Total Clients</p>
              <p className="text-xs text-gray-400 mt-1">
                Active clients
              </p>
            </div>
          </div>
        </div>

        {/* Wallet Balance */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 rounded-full bg-emerald-100 flex-shrink-0">
              <FaWallet className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">₹{stats.walletBalance}</p>
              <p className="text-xs sm:text-sm text-gray-500">Wallet Balance</p>
              <p className="text-xs text-gray-400 mt-1">
                Available balance
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={card.onClick}
              className="bg-white rounded-xl shadow-md p-6 cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-105 border border-gray-100"
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${card.color} flex items-center justify-center mb-4 text-white`}>
                {card.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">{card.title}</h3>
              <p className="text-2xl font-bold text-gray-900 mb-2">{card.count}</p>
              <p className="text-sm text-gray-500">{card.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Case Status Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Pending Cases */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Pending Cases</h3>
            <div className="p-2 bg-yellow-100 rounded-full">
              <FaHourglassHalf className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">{stats.pendingCases}</p>
          <p className="text-sm text-gray-500">Awaiting review or action</p>
        </div>

        {/* Active Cases */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Active Cases</h3>
            <div className="p-2 bg-blue-100 rounded-full">
              <FaBriefcase className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">{stats.activeCases}</p>
          <p className="text-sm text-gray-500">Currently being handled</p>
        </div>

        {/* Completed Cases */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800">Completed Cases</h3>
            <div className="p-2 bg-green-100 rounded-full">
              <FaCheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-2">{stats.completedCases}</p>
          <p className="text-sm text-gray-500">Successfully closed</p>
        </div>
      </div>

      {/* Profile Info Card */}
      {userInfo && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Advocate Profile</h3>
              <p className="text-sm opacity-90 mb-1">
                <strong>Name:</strong> {userInfo.name}
              </p>
              <p className="text-sm opacity-90 mb-1">
                <strong>ID:</strong> {userInfo.advocateId}
              </p>
              {userInfo.specialization && (
                <p className="text-sm opacity-90 mb-1">
                  <strong>Specialization:</strong> {userInfo.specialization}
                </p>
              )}
              {userInfo.experience && (
                <p className="text-sm opacity-90">
                  <strong>Experience:</strong> {userInfo.experience} {userInfo.experience === 1 ? 'Year' : 'Years'}
                </p>
              )}
            </div>
            <button
              onClick={() => navigate("/advocate/profile")}
              className="bg-white text-orange-600 px-6 py-2 rounded-lg font-medium hover:bg-orange-50 transition-colors"
            >
              View Profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvocateDashboard;

